import { Button } from '@progress/kendo-react-buttons'
import { Error, Label } from '@progress/kendo-react-labels'
import {
  Card,
  CardActions,
  CardBody,
  CardHeader,
  CardTitle,
  GridLayout,
  GridLayoutItem,
  StackLayout
} from '@progress/kendo-react-layout'
import {
  BranchSelect,
  SimulationModeDropDownList,
  ErrorMessages,
  ErrorMessage,
  Message
} from '../../../components/parts'
import { useAccessibleAuthority, usePageTitle } from '../../../components/hooks'
import { Authority } from '../../../gateway/system/Authority'
import { SimulationExecuteGateway } from '../../../gateway/simulation/SimulationExecuteGateway'
import { LinkInfoRes, PostSimulationExecuteReqParams } from '@gateway.Simulation'
import { useNavigate } from 'react-router-dom'
import { FC, useEffect, useState } from 'react'
import { useAppSelector, CommonActions, useAppDispatch } from '../../../stores'
import { MessageProvider } from '../../../services'
import { SimulationInfoGateway } from '../../../gateway/simulation/SimulationInfoGateway'
import { Marker } from '../../../components/elements'
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid'
import { Tooltip } from '@progress/kendo-react-tooltip'
import './SimulationExecution.css'

export const SimulationExecution: FC = () => {
  useAccessibleAuthority(Authority.Builder, Authority.Branch)
  usePageTitle('山積シミュレーション')
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  // モードの状態
  const [modeKbn, setModeKbn] = useState('1')
  // 支店の状態
  const [branchCdList, setBranchCdList] = useState<string[]>([])
  const userProp = useAppSelector((state) => state.common.userProp)
  // 連携情報
  const [linkInfo, setlinkInfo] = useState<LinkInfoRes[]>([])
  const [success, setSuccess] = useState(false)

  const [notificationMessages, setNotificationMessages] = useState<ErrorMessage[]>([])
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])
  const [timeErrorVisible, setTimeErrorVisible] = useState(false)
  const [errorMessageId, setErrorMessageId] = useState('')
  const [errorMessageArgs, setErrorMessageArgs] = useState([''])

  /**
   * 日時の整形
   * @param importDtm APIで取得した日時
   * @returns 整形した日時
   */
  const formattedDtm = (dtm: string | undefined | null) => {
    if (dtm === undefined || dtm === null) return null

    const splitT = dtm.split('T')
    const YYYY = splitT[0].split('-')[0]
    const MM = splitT[0].split('-')[1]
    const DD = splitT[0].split('-')[2]
    const hh = splitT[1].split(':')[0]
    const mm = splitT[1].split(':')[1]

    return `${YYYY}年${MM}月${DD}日  ${hh}:${mm}`
  }

  useEffect(() => {
    if (userProp !== undefined) {
      setBranchCdList([userProp.branchCode])
    }
  }, [userProp])

  useEffect(() => {
    /**
     * APIから情報を取得する
     */
    SimulationInfoGateway.getSimulationInfo()
      .then((res) => {
        if (res.error) {
          const resMessages = res.messages
          if (Array.isArray(resMessages) && resMessages.length > 0) {
            const messages = resMessages.map<ErrorMessage>((message: any) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(messages)
          }
        } else {
          const simulationInfoList = res.data.simulationInfoList.map(v => {
            return {
              ...v,
              startDtm: formattedDtm(v.startDtm),
              endDtm: formattedDtm(v.endDtm)
            }
          })
          setlinkInfo(simulationInfoList)
        }
      })
      .catch((e) => {
        throw e
      })
  }, [success])

  const opportunityProgressColor = (props: GridCellProps) => {
    // 進捗状況
    // eslint-disable-next-line react/prop-types
    const linkInfo: LinkInfoRes = props.dataItem
    const progressColor =
      // eslint-disable-next-line react/prop-types
      (linkInfo.startDtm !== null && linkInfo.endDtm === null && linkInfo.message === null)
        ? 'primary'
        // eslint-disable-next-line react/prop-types
        : (linkInfo.startDtm !== null && linkInfo.endDtm !== null)
            ? 'secondary'
            // eslint-disable-next-line react/prop-types
            : (linkInfo.startDtm !== null && linkInfo.endDtm === null && linkInfo.message !== null)
                ? 'accent'
                : undefined

    return progressColor !== undefined
      ? (<td style={{ textAlign: 'center' }}>
          <Marker themeColor={progressColor}>
            {/* eslint-disable-next-line react/prop-types */}
            {linkInfo.progressStatus}
          </Marker>
        </td>
        )
      : (
          <td></td>
        )
  }

  /**
   * モードの取得
   * @param value モード
   */
  const handleModeKbn = (value: string) => {
    setModeKbn(value)
  }
  /**
   * 支店の取得
   * @param value 支店
   */
  const handleBranchCd = (valueList: string[]) => {
    setBranchCdList(valueList)
  }
  /**
   * システム時間の取得
   * @returns 必要な時間フォーマット
   */
  const systemTime = () => {
    const month = new Date().getMonth() + 1
    const year = new Date().getFullYear()
    const showDate = month > 9 ? `${year}/${month}` : `${year}/0${month}`
    const neeDDate = month > 9 ? `${year}${month}` : `${year}0${month}`
    return { showDate, neeDDate }
  }

  /** イベントの処理を実行する */
  const apiSimulationExecuteGatwayHandle = () => {
    setTimeErrorVisible(false)
    // 必須チェック
    if (branchCdList.length === 0) {
      setErrorMessageId('E001')
      setErrorMessageArgs(['支店', '自動山積計算'])
      setTimeErrorVisible(true)
      return
    }
    dispatch(CommonActions.setIsLoading(true))
    // クエリパラメータ
    const reqParams: PostSimulationExecuteReqParams = {
      execInfo: {
        branch: branchCdList,
        simulationYm: systemTime().neeDDate,
        execMode: modeKbn
      },
      buildingInfo: {}
    }

    let flg: boolean = true
    SimulationExecuteGateway.postSimulationExecute(reqParams)
      .then((res) => {
        if (res.error) {
          // errorの場合
          flg = false
          const resMessages = res.messages
          if (Array.isArray(resMessages) && resMessages.length > 0) {
            const messages = resMessages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(messages)
          }
        }
      })
      .finally(() => {
        dispatch(CommonActions.setIsLoading(false))
        if (flg) {
          // 明細部を再検索する
          setSuccess(!success)
          // Notificationを表示する
          setNotificationMessages([
            {
              id: 'I017',
              content: MessageProvider.provide('I017', '自動山積計算')
            }
          ])
        }
      })
  }

  return (
    <div>
      <Card style={{ width: '40%', margin: '30px auto' }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <CardTitle>山積シミュレーション</CardTitle>
        </CardHeader>
        <CardBody>
          <GridLayout cols={[{ width: '20%' }]} gap={{ cols: 5, rows: 5 }}>
            <GridLayoutItem row={1} col={1}>
              <Label>モード</Label>
            </GridLayoutItem>
            <GridLayoutItem row={1} col={2}>
              <SimulationModeDropDownList
                value={modeKbn}
                onChange={handleModeKbn}
              />
            </GridLayoutItem>
            <GridLayoutItem row={2} col={1}>
              <Label>実行年月</Label>
            </GridLayoutItem>
            <GridLayoutItem row={2} col={2}>
              {systemTime().showDate}
            </GridLayoutItem>
            <GridLayoutItem row={3} col={1}>
              <Label>支店</Label>
            </GridLayoutItem>
            <GridLayoutItem row={3} col={2}>
              <BranchSelect
                multiple
                onChange={handleBranchCd}
                defaultValue={branchCdList}
                popupSettings={{ className: 'pcy-dropdownlist' }}
                autoClose={false}
              />
            </GridLayoutItem>
            <GridLayoutItem row={4} col={2}>
              {timeErrorVisible && (
                  <Error>
                    <Message id={errorMessageId} args={errorMessageArgs} />
                  </Error>
              )}
            </GridLayoutItem>
          </GridLayout>
        </CardBody>
        <CardActions layout="end">
          <StackLayout gap={5}>
            <Button
              style={{ minWidth: 120 }}
              onClick={() => {
                navigate(-1)
              }}
            >
              戻る
            </Button>
            <Button
              style={{ minWidth: 120 }}
              themeColor="primary"
              onClick={apiSimulationExecuteGatwayHandle}
            >
              実行
            </Button>
          </StackLayout>
        </CardActions>
      </Card>
      <Grid
        style={{ width: '90%', margin: '30px auto' }}
        sortable={false}
        filterable={false}
        scrollable='none'
        data={linkInfo}>
          <GridColumn
            title="支店"
            field="branchNm"
            width="80px"
          />
          <GridColumn
            title="開始日時"
            field="startDtm"
            width="170px"
          />
          <GridColumn
            title="終了日時"
            field="endDtm"
            width="170px"
          />
          <GridColumn
            title="実行結果"
            field="progressStatus"
            cell={opportunityProgressColor}
            width="75px"
            />
          <GridColumn
            title="メッセージ"
            field="message"
            width="calc(100% - 495px)"
            cell={(props: GridCellProps) => (
              <td>
                <Tooltip position='right'>
                    <span title={props.dataItem.message ?? ''} style={{ display: 'block', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                      {props.dataItem.message}
                    </span>
                </Tooltip>
              </td>
            )}
          />
      </Grid>
      <ErrorMessages type="notification" messages={notificationMessages} />
      <ErrorMessages type="dialog" messages={errorMessages} />
    </div>
  )
}
